/**
 * Builds the query string for navigating to signup while preserving OAuth params.
 * Strips and re-adds only `username`, `firstname`, and `lastname` so values such as
 * `redirect_uri` stay byte-for-byte identical (no `URLSearchParams` / full-stringify
 * double-encoding).
 *
 * `username` is only written when the incoming search already has a non-empty
 * `username` (then SSO can replace it). If the URL never had `username`, it is not
 * added from SSO.
 * `firstname` / `lastname` from SSO override when provided; otherwise the original
 * segments are copied from the current search.
 * When SSO `username` matches the URL value semantically, the original `username=…`
 * segment is kept (literal `+` stays `+`, `%2B` stays `%2B`). If it differs, the new
 * value is appended with encodeUsernameQueryValue (encodeURIComponent plus
 * literal `@` in the value).
 *
 * @param {string} searchString location.search (with or without leading ?)
 * @param {object} ssoIntegrationOptions from redirect.getSSOIntegrationOptions / siteParams
 * @returns {string} e.g. "?client_id=...&username=..."
 */
import {
  stripParamNames,
  extractRawQueryParamPair,
  encodeUsernameQueryValue,
} from './preserveQueryString';

function normalizeSearch(searchString) {
  if (!searchString) return '';
  return searchString.startsWith('?') ? searchString : `?${searchString}`;
}

function usernamePairHasValue(usernamePair) {
  if (!usernamePair || !usernamePair.includes('=')) return false;
  const v = usernamePair.slice(usernamePair.indexOf('=') + 1);
  return v.length > 0;
}

/** Decode query value for comparison; normalize space→+ for E.164 / form-urlencoded +. */
function semanticUsernameFromUrlValue(rawValue) {
  try {
    const decoded = decodeURIComponent(
      String(rawValue).replace(/\+/g, '%2B'),
    );
    return decoded.replace(/ /g, '+');
  } catch {
    return String(rawValue).replace(/ /g, '+');
  }
}

export function buildSignUpSearchFromLocation(searchString, ssoIntegrationOptions) {
  const raw = normalizeSearch(searchString);
  const sso = ssoIntegrationOptions || {};

  const usernamePair = extractRawQueryParamPair(raw, 'username');
  const hadUsernameInUrl = usernamePairHasValue(usernamePair);

  const base = stripParamNames(raw, ['username', 'firstname', 'lastname']);

  const additions = [];

  if (sso.firstname) {
    additions.push(`firstname=${encodeURIComponent(String(sso.firstname))}`);
  } else {
    const p = extractRawQueryParamPair(raw, 'firstname');
    if (p) additions.push(p);
  }

  if (sso.lastname) {
    additions.push(`lastname=${encodeURIComponent(String(sso.lastname))}`);
  } else {
    const p = extractRawQueryParamPair(raw, 'lastname');
    if (p) additions.push(p);
  }

  if (hadUsernameInUrl) {
    if (sso.username) {
      const u = String(sso.username).replace(/ /g, '+');
      const rawVal = usernamePair.slice(usernamePair.indexOf('=') + 1);
      if (semanticUsernameFromUrlValue(rawVal) === u) {
        additions.push(usernamePair);
      } else {
        additions.push(`username=${encodeUsernameQueryValue(u)}`);
      }
    } else if (usernamePair) {
      additions.push(usernamePair);
    }
  }

  const suffix = additions.join('&');
  if (!suffix) return base || '';
  if (!base) return `?${suffix}`;
  return `${base}&${suffix}`;
}
